import { useMemo } from 'react'

import {
  useFilters, useModals, useSetState,
} from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'
import type { PageContextPayload } from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import { generateUrls } from '@hooks/useProviderPriceList'

import BulkUpdateProviderPriceListsModal from '@modals/BulkUpdateProviderPriceListsModal'
import CreateOrEditProviderPriceListModal from '@modals/CreateOrEditProviderPriceListModal'

import type { ProviderPriceListModel } from '@models/types'

import PageContent from './components/PageContent'

const defaultState = {
  showBulkUpdateProviderPriceListsModal: false,
  showCreateOrEditProviderPriceListModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

// @ts-expect-error TS7006
const callbacks = (component: string, setState) => {
  const componentCallbacks = {
    BulkUpdateProviderPriceListsModal: {
      closeModal: () => setState({ showBulkUpdateProviderPriceListsModal: false }),
      bulkUpdateProviderPriceLists: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditProviderPriceListModal: {
      closeModal: () => setState({ showCreateOrEditProviderPriceListModal: false }),
      createProviderPriceList: async (payload: HandleCallbackActionParams) => {
        // @ts-expect-error TS2739
        const result: { success: boolean, data: ProviderPriceListModel } = await handleCallbackAction(payload)
        if (result.success){
          const { editProviderPriceListUrl } = generateUrls(result.data)
          window.location.hash = editProviderPriceListUrl
        }
      },
    },
  }

  // @ts-expect-error TS7053
  return componentCallbacks[component] || {}
}

const List = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showBulkUpdateProviderPriceListsModal,
    showCreateOrEditProviderPriceListModal,
  } = state

  const {
    callbacks: filterCallbacks,
    defaultFilters,
    pageFilters,
  } = useFilters()

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
    topModalKey,
  } = modalContext

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      ...filterCallbacks,
      showBulkUpdateProviderPriceListsModal: (payload: HandleCallbackActionParams) => {
        setModalData('BulkUpdateProviderPriceListsModal', payload)
        setState({ showBulkUpdateProviderPriceListsModal: true })
      },
      showCreateOrEditProviderPriceListModal: () => setState({ showCreateOrEditProviderPriceListModal: true }),
    },
    defaultFilters,
  }), [topModalKey])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent pageFilters={pageFilters} />

        <BulkUpdateProviderPriceListsModal
          callbacks={callbacks('BulkUpdateProviderPriceListsModal', setState)}
          showModal={showBulkUpdateProviderPriceListsModal}
        />

        <CreateOrEditProviderPriceListModal
          callbacks={callbacks('CreateOrEditProviderPriceListModal', setState)}
          showModal={showCreateOrEditProviderPriceListModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default List
